import { AlertTriangle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { type PayoutBatchWarning, PayoutBatchWarningCode } from "../../types/payoutBatches.types";
import { batchPath } from "../../utils/batchLinks";
import { BatchFeesUpdatedAlert } from "./BatchFeesUpdatedAlert";

export interface BatchWarningsBannerProps {
  warnings: PayoutBatchWarning[];
  /** Asks the Bre-B directory again for the keys it did not answer about; absent when the viewer cannot. */
  onRevalidate?: () => void;
  isRevalidating?: boolean;
}

/**
 * Batch-level warnings — advisory, never blocking. A repricing after a fee change gets its own alert with the
 * before/after amounts; the rest (same file uploaded before, ignored columns) share one list.
 */
export function BatchWarningsBanner({
  warnings,
  onRevalidate,
  isRevalidating = false,
}: BatchWarningsBannerProps) {
  const { t } = useTranslations("payoutBatches");
  const feesUpdated = warnings.find((warning) => warning.code === PayoutBatchWarningCode.FEES_UPDATED);
  const others = warnings.filter((warning) => warning.code !== PayoutBatchWarningCode.FEES_UPDATED);
  if (!feesUpdated && others.length === 0) return null;

  return (
    <div className="space-y-3">
      {feesUpdated && <BatchFeesUpdatedAlert warning={feesUpdated} />}
      {others.length > 0 && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          text={
            <ul className="space-y-1">
              {others.map((warning, index) => (
                <li key={`${warning.code}-${index}`} className="flex flex-wrap items-center gap-x-2">
                  <span>{warning.message}</span>
                  {warning.code === PayoutBatchWarningCode.SAME_FILE_UPLOADED_BEFORE && warning.batch_id && (
                    <Link
                      to={batchPath(warning.batch_id)}
                      className="font-medium underline underline-offset-2"
                    >
                      {t("warning_same_file_link")}
                    </Link>
                  )}
                  {warning.code === PayoutBatchWarningCode.BREB_KEYS_UNVERIFIED && onRevalidate && (
                    <ColocaButton
                      variant="default"
                      size="sm"
                      icon={RefreshCw}
                      loading={isRevalidating}
                      onClick={onRevalidate}
                    >
                      {t("action_revalidate")}
                    </ColocaButton>
                  )}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );
}
